import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { FileText, Search, Lock, ShieldCheck, Link2Off, X, Loader2 } from "lucide-react";

// "Send a document" — pick a library file; the caller inserts its download link into the reply.
export function DocumentPicker({ convId, onPick, onClose }) {
  const [q, setQ] = useState("");
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/conversations/${convId}/documents`, { params: { q } });
        if (alive) setDocs(r.data || []);
      } catch { if (alive) setDocs([]); }
    }, q ? 200 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [convId, q]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const pick = async (d) => { setBusy(d.id); try { await onPick(d); } finally { setBusy(null); } };

  return (
    <div data-testid="document-picker" className="absolute bottom-[76px] left-3 right-3 z-20 bg-white border border-sand-200 rounded-2xl shadow-xl overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-sand-100">
        <Search className="h-4 w-4 text-sand-400 shrink-0" />
        <input ref={inputRef} data-testid="document-picker-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search your AI Documents to text a download link…"
          className="flex-1 min-w-0 text-sm py-1.5 outline-none bg-transparent" />
        <button data-testid="document-picker-close" onClick={onClose} className="h-7 w-7 rounded-full flex items-center justify-center text-sand-400 hover:text-ink hover:bg-sand-100"><X className="h-4 w-4" /></button>
      </div>
      <div className="max-h-64 overflow-y-auto p-1.5">
        {docs === null ? (
          <div className="text-sm text-muted-foreground p-4 text-center flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : docs.length === 0 ? (
          <div className="text-sm text-muted-foreground p-4 text-center">{q ? "No documents match." : "No downloadable documents in this library yet."}</div>
        ) : docs.map((d) => {
          const restricted = (d.audiences || []).length > 0 && !(d.audiences || []).includes("anyone");
          const off = d.share_by_link === false;
          return (
            <button key={d.id} data-testid={`document-pick-${d.id}`} disabled={off || busy === d.id} onClick={() => pick(d)}
              title={off ? "Link sharing is off for this file (AI Documents → file menu)" : `Insert a download link to ${d.title}`}
              className="w-full text-left flex items-start gap-2.5 p-2.5 rounded-xl hover:bg-sand-50 transition-colors disabled:opacity-50 disabled:hover:bg-transparent">
              <FileText className="h-4 w-4 mt-0.5 text-redline-500 shrink-0" strokeWidth={1.75} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm text-ink truncate">{d.title}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                  {d.filename && d.filename !== d.title && <span className="truncate max-w-[200px]">{d.filename}</span>}
                  {restricted && <span className="inline-flex items-center gap-1 text-amber-800"><Lock className="h-3 w-3" /> {d.audiences.join(", ")}</span>}
                  {d.require_verification && <span className="inline-flex items-center gap-1 text-red-700"><ShieldCheck className="h-3 w-3" /> ID check</span>}
                  {off && <span className="inline-flex items-center gap-1"><Link2Off className="h-3 w-3" /> Link sharing off</span>}
                  {(d.tags || []).slice(0, 3).map((t) => <span key={t} className="rounded-full bg-sand-100 px-1.5">{t}</span>)}
                </div>
              </div>
              {busy === d.id && <Loader2 className="h-4 w-4 animate-spin text-ink/40 mt-0.5" />}
            </button>
          );
        })}
      </div>
      <div className="px-3 py-2 border-t border-sand-100 text-[11px] text-muted-foreground">
        The link goes into your reply so you can add a note, then Send. The AI also learns the customer's wording for this file.
      </div>
    </div>
  );
}
